import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

export type ThemeName = "system" | "ocean" | "ember" | "forest" | "paper";

type ThemeContextValue = {
    theme: ThemeName | null;
    overrideTheme: ThemeName | null;
    effectiveTheme: ThemeName | null;
    setTheme: (theme: ThemeName | null) => void;
    setOverrideTheme: (theme: ThemeName | null) => void;
};

const THEME_STORAGE_KEY = "gmav.theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function clearThemeClasses(root: HTMLElement): void {
    root.classList.remove(
        "theme-ocean",
        "theme-ember",
        "theme-forest",
        "theme-paper",
    );
}

function applyThemeClass(root: HTMLElement, theme: ThemeName | null): void {
    clearThemeClasses(root);
    if (!theme) return;
    root.classList.add(`theme-${theme}`);
}

export function ThemeProvider({
    children,
}: {
    children: React.ReactNode;
}): JSX.Element {
    const [theme, setThemeState] = useState<ThemeName | null>(null);
    const [overrideTheme, setOverrideThemeState] = useState<ThemeName | null>(
        null,
    );

    useEffect(() => {
        const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (
            raw === "system" ||
            raw === "ocean" ||
            raw === "ember" ||
            raw === "forest" ||
            raw === "paper"
        ) {
            setThemeState(raw);
        }
    }, []);

    const setTheme = useCallback((next: ThemeName | null) => {
        setThemeState(next);
        if (next) {
            window.localStorage.setItem(THEME_STORAGE_KEY, next);
        } else {
            window.localStorage.removeItem(THEME_STORAGE_KEY);
        }
    }, []);

    const setOverrideTheme = useCallback((next: ThemeName | null) => {
        setOverrideThemeState(next);
    }, []);

    const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
        if (typeof window === "undefined") return true;
        return (
            window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true
        );
    });

    useEffect(() => {
        if (typeof window === "undefined") return;

        const mq = window.matchMedia("(prefers-color-scheme: dark)");

        const onChange = (e: MediaQueryListEvent): void => {
            setSystemPrefersDark(e.matches);
        };

        setSystemPrefersDark(mq.matches);
        mq.addEventListener("change", onChange);

        return () => {
            mq.removeEventListener("change", onChange);
        };
    }, []);

    const systemTheme: ThemeName | null =
        theme === "system" ? (systemPrefersDark ? null : "paper") : theme;

    const effectiveTheme = overrideTheme ?? systemTheme;

    useEffect(() => {
        applyThemeClass(document.documentElement, effectiveTheme);
    }, [effectiveTheme]);

    const value = useMemo<ThemeContextValue>(() => {
        return {
            theme,
            overrideTheme,
            effectiveTheme,
            setTheme,
            setOverrideTheme,
        };
    }, [theme, overrideTheme, effectiveTheme, setTheme, setOverrideTheme]);

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error("useTheme must be used within ThemeProvider");
    }
    return ctx;
}
